import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { toast } from "react-toastify";
import SelectField from "../ui/SelectField";
import InputField from "../ui/InputField";
import TextAreaField from "../ui/TextAreaField";

const TransferModal = ({
  open,
  onClose,
  onSuccess,
  almacenes,
  token,
  API_URL,
}) => {
  const [articulosDisponibles, setArticulosDisponibles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    almacenOrigenId: "",
    almacenDestinoId: "",
    observaciones: "",
    items: [],
  });

  const [nuevoItem, setNuevoItem] = useState({
    codigo_item: "",
    articulo: "",
    cantidad: 1,
    precio_unitario: 0,
  });

  // 🔄 Cargar stock al cambiar almacén origen
  useEffect(() => {
    if (!formData.almacenOrigenId) {
      setArticulosDisponibles([]);
      return;
    }

    axios
      .get(`${API_URL}/almacenes/${formData.almacenOrigenId}/stock`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setArticulosDisponibles(res.data.stock || []))
      .catch(() => setArticulosDisponibles([]));
  }, [formData.almacenOrigenId, API_URL, token]);

  if (!open) return null;

  const handleAddItem = () => {
    if (!nuevoItem.codigo_item || nuevoItem.cantidad <= 0) {
      toast.error("Artículo inválido");
      return;
    }

    if (formData.items.some((i) => i.codigo_item === nuevoItem.codigo_item)) {
      toast.error("Artículo duplicado");
      return;
    }

    setFormData({
      ...formData,
      items: [...formData.items, { ...nuevoItem }],
    });

    setNuevoItem({
      codigo_item: "",
      articulo: "",
      cantidad: 1,
      precio_unitario: 0,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (
      !formData.almacenOrigenId ||
      !formData.almacenDestinoId ||
      formData.items.length === 0
    ) {
      toast.error("Complete todos los datos");
      return;
    }

    if (formData.almacenOrigenId === formData.almacenDestinoId) {
      toast.error("Origen y destino no pueden ser iguales");
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/transferencias`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Transferencia creada ✅");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al crear transferencia",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-xl font-semibold">🔄 Nueva Transferencia</h3>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          {/* Almacenes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="Almacén Origen *"
              name="almacenOrigenId"
              value={formData.almacenOrigenId}
              options={almacenes}
              displayFn={(a) => a.nombre}
              placeholder="Seleccionar almacén origen"
              onChange={(e) =>
                setFormData({ ...formData, almacenOrigenId: e.target.value })
              }
            />

            <SelectField
              label="Almacén Destino *"
              name="almacenDestinoId"
              value={formData.almacenDestinoId}
              options={almacenes}
              displayFn={(a) => a.nombre}
              placeholder="Seleccionar almacén destino"
              onChange={(e) =>
                setFormData({ ...formData, almacenDestinoId: e.target.value })
              }
            />
          </div>

          {/* Observaciones */}
          <TextAreaField
            label="Observaciones"
            name="observaciones"
            value={formData.observaciones}
            onChange={(e) =>
              setFormData({ ...formData, observaciones: e.target.value })
            }
          />

          {/* Artículos */}
          {formData.almacenOrigenId && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div className="md:col-span-2">
                  <SelectField
                    label="Artículo"
                    name="codigo_item"
                    value={nuevoItem.codigo_item}
                    options={articulosDisponibles}
                    valueKey="codigo_item" // 🔥 AQUÍ ESTÁ LA CLAVE
                    placeholder="Seleccionar artículo"
                    displayFn={(a) => `${a.articulo} (Stock ${a.stock_actual})`}
                    onChange={(e) => {
                      if (!e.target.raw) return;

                      const art = e.target.raw;

                      setNuevoItem({
                        codigo_item: art.codigo_item,
                        articulo: art.articulo,
                        cantidad: 1,
                        precio_unitario: art.precio_unitario || 0,
                      });
                    }}
                  />
                </div>

                <InputField
                  label="Cantidad"
                  type="number"
                  min={1}
                  value={nuevoItem.cantidad}
                  onChange={(e) =>
                    setNuevoItem({
                      ...nuevoItem,
                      cantidad: Number(e.target.value),
                    })
                  }
                />

                <InputField
                  label="Precio unitario"
                  value={nuevoItem.precio_unitario}
                  readOnly
                />

                <Button
                  type="button"
                  onClick={handleAddItem}
                  className="bg-green-600 text-white"
                >
                  +
                </Button>
              </div>
            </div>
          )}

          {/* Items agregados */}
          {formData.items.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">
                      Artículo
                    </th>
                    <th className="px-4 py-3 text-center font-semibold">
                      Cantidad
                    </th>
                    <th className="px-4 py-3 text-right font-semibold">
                      Acción
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {formData.items.map((i, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {i.articulo}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex min-w-[32px] justify-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                          {i.cantidad}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              items: formData.items.filter((_, x) => x !== idx),
                            })
                          }
                          className="
                inline-flex items-center justify-center
                rounded-md
                px-2 py-1
                text-sm
                text-red-600
                hover:bg-red-50
                hover:text-red-700
                transition
              "
                          title="Eliminar artículo"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-blue-600 text-white"
            >
              {saving ? "Creando..." : "Crear transferencia"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferModal;
